/**
 * Five feature spreads (PR11). Replaces the flat 01-06 trust signals with
 * App-Store-grade sections, copy lifted verbatim from the locked Figma frame
 * (https://www.figma.com/design/OI0XV1kp7KkCdAItuWOcuF, node 4396:2).
 *
 * Each section is its own composition — alternating asymmetry, one centered —
 * with a faithful Mac-app visual. Section 1 absorbs the ConsecutiveReveal
 * block; Section 5 uses the Figma's Smith v Acme production-log data.
 */

import { ConsecutiveReveal } from "./ConsecutiveReveal";
import styles from "./Features.module.css";

interface FeaturesProps {
  onOpenPrivacy: () => void;
}

const SMITH_FILES = [
  { name: "Complaint.pdf", range: "SMITH-000001 → 000047", size: "12.4 MB" },
  { name: "Defendant_Response.pdf", range: "SMITH-000048 → 000189", size: "38.1 MB" },
  { name: "Deposition_Hartman.pdf", range: "SMITH-000190 → 000312", size: "29.6 MB" },
  { name: "Email_Exhibits_A–N.pdf", range: "SMITH-000313 → 000528", size: "54.2 MB" },
  { name: "Plaintiff_Production.pdf", range: "SMITH-000529 → 000706", size: "41.8 MB" },
];

export function Features({ onOpenPrivacy }: FeaturesProps) {
  return (
    <div className={styles.features}>
      {/* Section 1 — Number every file. In one pass. */}
      <section className={styles.section} aria-labelledby="f1-title">
        <header className={styles.headerCentered}>
          <h2 id="f1-title" className={styles.title}>
            Number every file. <span className={styles.emph}>In one pass.</span>
          </h2>
          <p className={styles.sub}>
            Consecutive Bates stamps across an entire document set, in one drop.
          </p>
        </header>
        <ConsecutiveReveal />
      </section>

      {/* Section 2 — Your client files never leave your Mac. (text left, visual right) */}
      <section className={`${styles.section} ${styles.split}`} aria-labelledby="f2-title">
        <div className={styles.copy}>
          <h2 id="f2-title" className={styles.title}>
            Your client files <span className={styles.emph}>never leave your Mac.</span>
          </h2>
          <p className={styles.sub}>
            100% local processing. No uploads. No cloud. No data collection.
            Built for confidential legal work.
          </p>
          <button type="button" className={styles.linkAction} onClick={onOpenPrivacy}>
            Read the privacy report
          </button>
        </div>
        <div className={styles.visual}>
          <PrivacyDiagram />
        </div>
      </section>

      {/* Section 3 — Production-grade. Down to the detail. (visual left, text right) */}
      <section className={`${styles.section} ${styles.split} ${styles.reverse}`} aria-labelledby="f3-title">
        <div className={styles.copy}>
          <h2 id="f3-title" className={styles.title}>
            Production-grade. <span className={styles.emph}>Down to the detail.</span>
          </h2>
          <p className={styles.sub}>
            Placement options. Confidential and privileged endorsement lines.
            Any prefix. Any installed font.
          </p>
        </div>
        <div className={styles.visual}>
          <InspectorCutout />
        </div>
      </section>

      {/* Section 4 — Buy once. No subscription. Ever. (centered, price contrast) */}
      <section className={`${styles.section} ${styles.centered}`} aria-labelledby="f4-title">
        <header className={styles.headerCentered}>
          <h2 id="f4-title" className={styles.title}>
            Buy once. <span className={styles.emph}>No subscription. Ever.</span>
          </h2>
          <p className={styles.sub}>
            First batch free. Any files. Any pages. No watermarks.
          </p>
        </header>
        <div className={styles.priceVisual}>
          <span className={styles.priceOld}>
            <span className={styles.priceOldLabel}>Subscription PDF suites</span>
            <span className={styles.priceOldNumber}>~$240/year</span>
            <span className={styles.priceOldForever}>every year, forever</span>
          </span>
          <span className={styles.priceArrow} aria-hidden="true">→</span>
          <span className={styles.priceNew}>
            <span className={styles.priceNewLabel}>Imprint for Mac</span>
            <span className={styles.priceNewNumber}>US$29.99</span>
            <span className={styles.priceNewQual}>one time</span>
          </span>
        </div>
      </section>

      {/* Section 5 — Court-ready output. Originals untouched. (text left, file list right) */}
      <section className={`${styles.section} ${styles.split}`} aria-labelledby="f5-title">
        <div className={styles.copy}>
          <h2 id="f5-title" className={styles.title}>
            Court-ready output. <span className={styles.emph}>Originals untouched.</span>
          </h2>
          <p className={styles.sub}>
            Stamps baked into the page, not floating annotations. Production log
            included.
          </p>
        </div>
        <div className={styles.visual}>
          <ProductionList />
        </div>
      </section>
    </div>
  );
}

function PrivacyDiagram() {
  return (
    <div className={styles.privacyDiagram} aria-hidden="true">
      <div className={styles.macFrame}>
        <span className={styles.macLabel}>Your Mac</span>
        <div className={styles.macInside}>
          <span className={styles.macChip}>Your PDF</span>
          <span className={styles.macArrow}>→</span>
          <span className={styles.macChip}>Imprint</span>
          <span className={styles.macArrow}>→</span>
          <span className={styles.macChip}>Stamped PDF</span>
        </div>
        <span className={styles.macBoundary}>nothing crosses this line</span>
      </div>
      <div className={styles.cloudCross}>
        <span className={styles.crossOut}>No cloud · No uploads · No collection</span>
      </div>
    </div>
  );
}

function InspectorCutout() {
  return (
    <div className={styles.inspector} aria-hidden="true">
      <div className={styles.inspectorTitle}>Configuration</div>
      <div className={styles.inspectorField}>
        <span className={styles.inspectorLabel}>Prefix</span>
        <div className={styles.inspectorInput}>SMITH-</div>
      </div>
      <div className={styles.inspectorRow}>
        <div className={styles.inspectorField}>
          <span className={styles.inspectorLabel}>Start</span>
          <div className={styles.inspectorInput}>1</div>
        </div>
        <div className={styles.inspectorField}>
          <span className={styles.inspectorLabel}>Digits</span>
          <div className={styles.inspectorInput}>6</div>
        </div>
      </div>
      <div className={styles.inspectorField}>
        <span className={styles.inspectorLabel}>Placement</span>
        <div className={styles.inspectorPositions}>
          <div className={styles.posBtn}>Top Left</div>
          <div className={styles.posBtn}>Top Center</div>
          <div className={styles.posBtn}>Top Right</div>
          <div className={styles.posBtn}>Bottom Left</div>
          <div className={styles.posBtn}>Bottom Center</div>
          <div className={`${styles.posBtn} ${styles.posActive}`}>Bottom Right</div>
        </div>
      </div>
      <div className={styles.inspectorField}>
        <span className={styles.inspectorLabel}>Font</span>
        <div className={styles.inspectorInput}>Cochin</div>
      </div>
      <div className={styles.inspectorField}>
        <span className={styles.inspectorLabel}>Theme</span>
        <div className={styles.inspectorThemes}>
          <div className={styles.inspectorThemeOption}>
            <span className={styles.themeChip}>SMIT</span>
            <span className={styles.inspectorThemeName}>White</span>
          </div>
          <div className={styles.inspectorThemeOption}>
            <span className={`${styles.themeChip} ${styles.themeDark}`}>SMIT</span>
            <span className={styles.inspectorThemeName}>Black</span>
          </div>
          <div className={styles.inspectorThemeOption}>
            <span className={`${styles.themeChip} ${styles.themeStroke}`}>SMIT</span>
            <span className={styles.inspectorThemeName}>White outline</span>
          </div>
          <div className={styles.inspectorThemeOption}>
            <span className={`${styles.themeChip} ${styles.themeDark} ${styles.themeStroke}`}>SMIT</span>
            <span className={styles.inspectorThemeName}>Black outline</span>
          </div>
        </div>
      </div>
      <div className={styles.inspectorField}>
        <span className={styles.inspectorLabel}>Endorsement</span>
        <div className={styles.inspectorTextarea}>CONFIDENTIAL</div>
      </div>
      <div className={styles.inspectorSample}>
        <span className={styles.inspectorLabel}>Sample</span>
        <span className={styles.inspectorSampleChip}>
          <strong>SMITH-000001</strong>
          <em>CONFIDENTIAL</em>
        </span>
      </div>
    </div>
  );
}

function ProductionList() {
  return (
    <div className={styles.production} aria-hidden="true">
      <div className={styles.productionChrome}>
        <span className={styles.dot} />
        <span className={styles.dot} />
        <span className={styles.dot} />
        <span className={styles.productionTitle}>Smith v Acme — Production 001</span>
      </div>
      <div className={styles.productionPath}>Desktop › Imprint Output › Smith v Acme</div>
      <div className={styles.productionHead}>
        <span>NAME</span>
        <span>BATES RANGE</span>
        <span>SIZE</span>
      </div>
      {SMITH_FILES.map((f) => (
        <div key={f.name} className={styles.productionRow}>
          <span className={styles.productionName}>
            <span className={styles.productionIcon}>PDF</span>
            <span>{f.name}</span>
          </span>
          <span className={styles.productionRange}>{f.range}</span>
          <span className={styles.productionSize}>{f.size}</span>
        </div>
      ))}
      <div className={styles.productionRow}>
        <span className={styles.productionName}>
          <span className={`${styles.productionIcon} ${styles.productionIconCsv}`}>CSV</span>
          <span>imprint-production-log.csv</span>
        </span>
        <span className={styles.productionRange}>Production manifest</span>
        <span className={styles.productionSize}>2 KB</span>
      </div>
    </div>
  );
}
